import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import {
  FiArrowLeft,
  FiCalendar,
  FiDownload,
  FiDollarSign,
  FiEdit,
  FiFileText,
  FiMail,
  FiTrash2,
  FiUser,
} from 'react-icons/fi';
import type { IconType } from 'react-icons';
import axios from 'axios';
import { formatDateOnlyUTC } from '../utils/dateOnly';
import { useAuth } from '../contexts/AuthContext';
import { KpiCard } from './Dashboard/kpi/KpiCard';

interface InvoiceItem {
  id: string;
  item_id?: string;
  name: string;
  description?: string;
  qty: number;
  rate: number;
  amount: number;
  is_taxable?: boolean;
}

interface InvoiceCustomer {
  id: string;
  first_name: string;
  last_name: string;
  display_name?: string;
  company_name?: string;
  email: string;
}

interface InvoiceAccountSummary {
  balance_forward: number;
  payments_credits_since_last_invoice: number;
  new_charges: number;
  total_amount_due: number;
}

interface Invoice {
  id: string;
  number: string;
  customer_id: string;
  issue_date: string;
  due_date: string;
  status: string;
  sub_total: number;
  tax_total: number;
  balance_due: number;
  terms?: string;
  customer?: InvoiceCustomer;
  items?: InvoiceItem[];
  account_summary?: InvoiceAccountSummary;
}

const parseDownloadFilename = (contentDisposition?: string, fallbackName?: string) => {
  if (!contentDisposition) return fallbackName;

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const plainMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  if (plainMatch?.[1]) {
    return plainMatch[1];
  }

  return fallbackName;
};

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const getStatusBadgeClassName = (status: string | undefined) => {
  switch ((status || '').toLowerCase()) {
    case 'open':
      return 'border-indigo-200 bg-indigo-50 text-indigo-700';
    case 'overdue':
      return 'border-red-200 bg-red-50 text-red-700';
    case 'partial_open':
      return 'border-amber-200 bg-amber-50 text-amber-700';
    case 'partial_over':
      return 'border-orange-200 bg-orange-50 text-orange-700';
    case 'closed':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700';
    default:
      return 'border-gray-200 bg-gray-50 text-gray-700';
  }
};

const formatStatusLabel = (status: string | undefined) => {
  if (!status) return 'Unknown';
  return status.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

const getCustomerDisplayName = (customer?: InvoiceCustomer) => {
  if (!customer) return 'Unknown Customer';
  if (customer.display_name) return customer.display_name;
  const fullName = `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
  return customer.company_name || fullName || 'Unknown Customer';
};

const DetailListRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex items-start justify-between gap-4 py-3">
    <dt className="text-sm text-gray-500 dark:text-gray-400">{label}</dt>
    <dd className="text-sm font-semibold text-gray-900 dark:text-white text-right">{value}</dd>
  </div>
);

const SectionCard = ({
  title,
  subtitle,
  children,
  rightSlot,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  rightSlot?: React.ReactNode;
}) => (
  <section className="rounded-2xl border border-gray-200 bg-white p-5 elev-1 dark:border-neutral-800 dark:bg-neutral-900">
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{title}</h2>
        {subtitle ? <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{subtitle}</p> : null}
      </div>
      {rightSlot}
    </div>
    <div className="mt-5">{children}</div>
  </section>
);

const StateCard = ({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: React.ReactNode;
}) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center elev-1 dark:border-neutral-800 dark:bg-neutral-900">
    <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-sedra/10 text-sedra dark:bg-sedra/20">
      <FiFileText className="h-6 w-6" aria-hidden="true" />
    </div>
    <h1 className="mt-4 text-xl font-extrabold tracking-tight text-gray-900 dark:text-white">{title}</h1>
    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{description}</p>
    {action ? <div className="mt-6">{action}</div> : null}
  </div>
);

const InvoiceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { token, isCookieMode, API_BASE_URL, csrfToken } = useAuth();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    if (id) {
      fetchInvoice();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const flashMessage = (location.state as { successMessage?: string } | null)?.successMessage;
    if (!flashMessage) return;
    setSuccessMessage(flashMessage);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      setError(null);
      const headers: Record<string, string> = {};
      if (!isCookieMode && token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await axios.get(`${API_BASE_URL}/invoices/${id}`, {
        headers,
        withCredentials: isCookieMode,
      });
      setInvoice(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!invoice) return;

    try {
      setDeleting(true);
      setError(null);
      const headers: Record<string, string> = {};
      if (!isCookieMode && token) {
        headers.Authorization = `Bearer ${token}`;
      }
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }

      await axios.delete(`${API_BASE_URL}/invoices/${invoice.id}`, {
        headers,
        withCredentials: isCookieMode,
      });
      setDeleteDialogOpen(false);
      navigate('/invoices?refresh=true');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete invoice');
      setDeleting(false);
    }
  };

  const doDownloadInvoicePDF = async (template: string = 'gptTest') => {
    if (!invoice) return;

    try {
      setDownloading(true);
      setError(null);
      const headers: Record<string, string> = {};
      if (!isCookieMode && token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await axios.get(`${API_BASE_URL}/invoices/${invoice.id}/pdf?template=${template}`, {
        headers,
        responseType: 'blob',
        withCredentials: isCookieMode,
      });

      const fileName = parseDownloadFilename(
        response.headers['content-disposition'],
        `invoice-${invoice.number}.pdf`
      ) || `invoice-${invoice.number}.pdf`;

      downloadBlob(new Blob([response.data]), fileName);
    } catch (_err: any) {
      setError('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  const doDownloadEnvelopePDF = async () => {
    if (!invoice) return;

    try {
      setDownloading(true);
      setError(null);
      const headers: Record<string, string> = {};
      if (!isCookieMode && token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await axios.get(`${API_BASE_URL}/invoices/${invoice.id}/envelope-pdf`, {
        headers,
        responseType: 'blob',
        withCredentials: isCookieMode,
      });

      const fileName = parseDownloadFilename(
        response.headers['content-disposition'],
        `envelope-${invoice.number}.pdf`
      ) || `envelope-${invoice.number}.pdf`;

      downloadBlob(new Blob([response.data]), fileName);
    } catch (_err: any) {
      setError('Failed to download envelope PDF');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadInvoiceOnly = async () => {
    setDownloadDialogOpen(false);
    await doDownloadInvoicePDF('gptTest');
  };

  const handleDownloadInvoiceAndEnvelope = async () => {
    setDownloadDialogOpen(false);
    await doDownloadInvoicePDF('gptTest');
    await doDownloadEnvelopePDF();
  };

  const handleBackNavigation = () => {
    const customerId = invoice?.customer?.id || invoice?.customer_id;
    if (customerId) {
      navigate(`/customers/${customerId}`);
      return;
    }
    navigate('/invoices');
  };

  const invoiceTotal = useMemo(() => {
    if (!invoice) return 0;
    return Number(invoice.sub_total || 0) + Number(invoice.tax_total || 0);
  }, [invoice]);

  const metricCards: Array<{ label: string; value: string; icon: IconType; accent: 'sedra' | 'amber' | 'red' | 'green' }> =
    invoice
      ? [
          {
            label: 'Issue Date',
            value: formatDateOnlyUTC(invoice.issue_date),
            icon: FiCalendar,
            accent: 'sedra',
          },
          {
            label: 'Due Date',
            value: formatDateOnlyUTC(invoice.due_date),
            icon: FiCalendar,
            accent: 'amber',
          },
          {
            label: 'Invoice Total',
            value: formatCurrency(invoiceTotal),
            icon: FiDollarSign,
            accent: 'green',
          },
          {
            label: 'Balance Due',
            value: formatCurrency(invoice.balance_due),
            icon: FiDollarSign,
            accent: invoice.balance_due > 0 ? 'red' : 'green',
          },
        ]
      : [];

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-8 lg:px-8">
        <StateCard
          title="Loading invoice"
          description="Fetching the latest invoice details and financial summary."
          action={<CircularProgress size={28} />}
        />
      </div>
    );
  }

  if (error && !invoice) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-8 lg:px-8">
        <StateCard
          title="Could not load invoice"
          description={error}
          action={
            <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
              <button
                type="button"
                onClick={handleBackNavigation}
                className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200"
              >
                Back
              </button>
              <button
                type="button"
                onClick={fetchInvoice}
                className="rounded-xl bg-sedra px-4 py-2.5 text-sm font-semibold text-white hover:bg-sedra-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra"
              >
                Try Again
              </button>
            </div>
          }
        />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-8 lg:px-8">
        <StateCard
          title="Invoice not found"
          description="The invoice may have been removed or you may no longer have access to it."
          action={
            <button
              type="button"
              onClick={() => navigate('/invoices')}
              className="rounded-xl bg-sedra px-4 py-2.5 text-sm font-semibold text-white hover:bg-sedra-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra"
            >
              Back to invoices
            </button>
          }
        />
      </div>
    );
  }

  const statusBadgeClassName = getStatusBadgeClassName(invoice.status);
  const customerName = getCustomerDisplayName(invoice.customer);
  const accountSummary = invoice.account_summary;

  return (
    <div className="mx-auto max-w-7xl px-6 py-8 lg:px-8">
      {successMessage ? (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : null}

      <section className="rounded-2xl border border-gray-200 bg-white p-5 elev-1 dark:border-neutral-800 dark:bg-neutral-900">
        <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
          <div className="min-w-0">
            <button
              type="button"
              onClick={handleBackNavigation}
              className="inline-flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200"
            >
              <FiArrowLeft className="h-4 w-4" aria-hidden="true" />
              Back to customer
            </button>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-3xl">
                Invoice #{invoice.number}
              </h1>
              <span
                className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide ${statusBadgeClassName}`}
              >
                {formatStatusLabel(invoice.status)}
              </span>
            </div>

            <p className="mt-2 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
              Review billing details, customer information, line items, and the current amount due in one place.
            </p>

            <div className="mt-6 rounded-2xl border border-sedra/20 bg-sedra/5 p-4 dark:border-sedra/30 dark:bg-sedra/10">
              <div className="text-xs font-semibold uppercase tracking-wide text-sedra">Current balance due</div>
              <div className="mt-1 text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white">
                {formatCurrency(invoice.balance_due)}
              </div>
              <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Due {formatDateOnlyUTC(invoice.due_date)} for {customerName}
              </div>
            </div>
          </div>

          <div className="flex w-full flex-col gap-3 sm:flex-row xl:w-auto xl:flex-col">
            <button
              type="button"
              onClick={() => setDownloadDialogOpen(true)}
              disabled={downloading}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-sedra px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-sedra-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra disabled:cursor-not-allowed disabled:opacity-60"
            >
              {downloading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <FiDownload className="h-4 w-4" aria-hidden="true" />}
              {downloading ? 'Preparing files...' : 'Download PDF'}
            </button>

            <button
              type="button"
              onClick={() => navigate(`/invoices/${invoice.id}/edit`)}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200"
            >
              <FiEdit className="h-4 w-4" aria-hidden="true" />
              Edit invoice
            </button>

            <button
              type="button"
              onClick={() => setDeleteDialogOpen(true)}
              disabled={deleting}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-700 transition-colors hover:bg-red-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-red-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-900/60 dark:bg-red-950/20 dark:text-red-300"
            >
              {deleting ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : <FiTrash2 className="h-4 w-4" aria-hidden="true" />}
              {deleting ? 'Deleting...' : 'Delete invoice'}
            </button>
          </div>
        </div>
      </section>

      <section className="mt-6 grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
        {metricCards.map((card) => (
          <KpiCard
            key={card.label}
            label={card.label}
            value={card.value}
            icon={card.icon}
            accent={card.accent}
          />
        ))}
      </section>

      <div className="mt-6 grid gap-6 xl:grid-cols-12">
        <div className="space-y-6 xl:col-span-8">
          <SectionCard title="Overview" subtitle="Invoice and bill-to details">
            <div className="grid gap-6 lg:grid-cols-2">
              <div className="rounded-2xl border border-gray-200 bg-gray-50/70 p-4 dark:border-neutral-800 dark:bg-neutral-950/40">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                  <FiFileText className="h-4 w-4 text-sedra" aria-hidden="true" />
                  Invoice details
                </div>
                <dl className="mt-3 divide-y divide-gray-200 dark:divide-neutral-800">
                  <DetailListRow label="Invoice number" value={invoice.number} />
                  <DetailListRow label="Issue date" value={formatDateOnlyUTC(invoice.issue_date)} />
                  <DetailListRow label="Due date" value={formatDateOnlyUTC(invoice.due_date)} />
                  <DetailListRow label="Status" value={formatStatusLabel(invoice.status)} />
                </dl>
              </div>

              <div className="rounded-2xl border border-gray-200 bg-gray-50/70 p-4 dark:border-neutral-800 dark:bg-neutral-950/40">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                  <FiUser className="h-4 w-4 text-sedra" aria-hidden="true" />
                  Bill to
                </div>
                <dl className="mt-3 divide-y divide-gray-200 dark:divide-neutral-800">
                  <DetailListRow label="Customer" value={customerName} />
                  {invoice.customer?.company_name ? (
                    <DetailListRow label="Company" value={invoice.customer.company_name} />
                  ) : null}
                  <DetailListRow
                    label="Email"
                    value={
                      invoice.customer?.email ? (
                        <a href={`mailto:${invoice.customer.email}`} className="text-sedra hover:underline">
                          {invoice.customer.email}
                        </a>
                      ) : (
                        'No email on file'
                      )
                    }
                  />
                  <DetailListRow label="Customer ID" value={invoice.customer?.id || invoice.customer_id} />
                </dl>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            title="Line Items"
            subtitle={`${invoice.items?.length || 0} item${invoice.items?.length === 1 ? '' : 's'} on this invoice`}
            rightSlot={
              <div className="inline-flex items-center gap-2 rounded-full bg-sedra/10 px-3 py-1 text-xs font-semibold text-sedra dark:bg-sedra/20">
                <FiMail className="h-3.5 w-3.5" aria-hidden="true" />
                Ready for download
              </div>
            }
          >
            {invoice.items && invoice.items.length > 0 ? (
              <div className="overflow-hidden rounded-2xl border border-gray-200 dark:border-neutral-800">
                <div className="hidden grid-cols-[minmax(0,1.8fr)_120px_140px_140px] gap-4 border-b border-gray-200 bg-gray-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:border-neutral-800 dark:bg-neutral-950/40 dark:text-gray-400 md:grid">
                  <span>Item</span>
                  <span className="text-right">Qty</span>
                  <span className="text-right">Rate</span>
                  <span className="text-right">Total</span>
                </div>

                <div className="divide-y divide-gray-200 dark:divide-neutral-800">
                  {invoice.items.map((item, index) => (
                    <div
                      key={item.id || `${item.name}-${index}`}
                      className="grid gap-4 px-4 py-4 md:grid-cols-[minmax(0,1.8fr)_120px_140px_140px] md:items-start"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                          {item.name || item.description || `Item ${index + 1}`}
                        </div>
                        {item.description ? (
                          <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">{item.description}</div>
                        ) : null}
                      </div>

                      <div className="flex items-center justify-between gap-4 md:block md:text-right">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 md:hidden">
                          Qty
                        </span>
                        <span className="text-sm font-semibold tabular-nums text-gray-900 dark:text-white">{item.qty}</span>
                      </div>

                      <div className="flex items-center justify-between gap-4 md:block md:text-right">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 md:hidden">
                          Rate
                        </span>
                        <span className="text-sm font-semibold tabular-nums text-gray-900 dark:text-white">
                          {formatCurrency(item.rate)}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-4 md:block md:text-right">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 md:hidden">
                          Total
                        </span>
                        <span className="text-sm font-extrabold tabular-nums text-gray-900 dark:text-white">
                          {formatCurrency(item.amount)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-gray-300 px-4 py-10 text-center text-sm text-gray-500 dark:border-neutral-700 dark:text-gray-400">
                No items were found on this invoice.
              </div>
            )}
          </SectionCard>
        </div>

        <aside className="space-y-6 xl:col-span-4">
          <div className="xl:sticky xl:top-6 space-y-6">
            <SectionCard title="Financial Summary" subtitle="Charges and current amount due">
              <dl className="divide-y divide-gray-200 dark:divide-neutral-800">
                <DetailListRow label="Subtotal" value={formatCurrency(invoice.sub_total)} />
                <DetailListRow label="Tax" value={formatCurrency(invoice.tax_total)} />
                <DetailListRow label="Invoice total" value={formatCurrency(invoiceTotal)} />
                <DetailListRow
                  label="Balance due"
                  value={<span className="text-base font-extrabold text-red-600 dark:text-red-400">{formatCurrency(invoice.balance_due)}</span>}
                />
              </dl>
            </SectionCard>

            {accountSummary ? (
              <SectionCard title="Account Summary" subtitle="Customer balance context around this invoice">
                <dl className="divide-y divide-gray-200 dark:divide-neutral-800">
                  <DetailListRow label="Balance forward" value={formatCurrency(accountSummary.balance_forward)} />
                  <DetailListRow
                    label="Payments and credits"
                    value={`-${formatCurrency(accountSummary.payments_credits_since_last_invoice)}`}
                  />
                  <DetailListRow label="New charges" value={formatCurrency(accountSummary.new_charges)} />
                  <DetailListRow
                    label="Total amount due"
                    value={
                      accountSummary.total_amount_due < 0 ? (
                        <span className="text-base font-extrabold text-emerald-600 dark:text-emerald-400">
                          {formatCurrency(Math.abs(accountSummary.total_amount_due))} credit
                        </span>
                      ) : (
                        <span className="text-base font-extrabold text-red-600 dark:text-red-400">
                          {formatCurrency(accountSummary.total_amount_due)}
                        </span>
                      )
                    }
                  />
                </dl>
              </SectionCard>
            ) : null}

            {invoice.terms ? (
              <SectionCard title="Terms and Notes" subtitle="Additional billing details attached to this invoice">
                <div className="rounded-2xl border border-gray-200 bg-gray-50/70 p-4 text-sm leading-6 text-gray-700 dark:border-neutral-800 dark:bg-neutral-950/40 dark:text-gray-300">
                  <pre className="whitespace-pre-wrap font-sans">{invoice.terms}</pre>
                </div>
              </SectionCard>
            ) : null}
          </div>
        </aside>
      </div>

      <DownloadDialog
        open={downloadDialogOpen}
        onClose={() => setDownloadDialogOpen(false)}
        onInvoiceOnly={handleDownloadInvoiceOnly}
        onInvoiceAndEnvelope={handleDownloadInvoiceAndEnvelope}
      />

      <DeleteInvoiceDialog
        open={deleteDialogOpen}
        invoiceNumber={invoice.number}
        deleting={deleting}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

const DownloadDialog = ({
  open,
  onClose,
  onInvoiceOnly,
  onInvoiceAndEnvelope,
}: {
  open: boolean;
  onClose: () => void;
  onInvoiceOnly: () => void;
  onInvoiceAndEnvelope: () => void;
}) => (
  <Dialog open={open} onClose={onClose} aria-labelledby="download-dialog-title" fullWidth maxWidth="sm">
    <DialogTitle id="download-dialog-title" sx={{ fontWeight: 800 }}>
      Download invoice files
    </DialogTitle>
    <DialogContent>
      <div className="space-y-3 pt-2">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Choose whether to download just the invoice PDF or include the matching #10 mailing envelope.
        </p>
        <div className="rounded-xl border border-sedra/20 bg-sedra/5 p-3 text-sm text-sedra dark:border-sedra/30 dark:bg-sedra/10">
          Invoice + Envelope will download both files one after the other.
        </div>
      </div>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 3 }}>
      <Button onClick={onClose}>Cancel</Button>
      <Button onClick={onInvoiceOnly} variant="outlined">
        Invoice only
      </Button>
      <Button
        onClick={onInvoiceAndEnvelope}
        variant="contained"
        sx={{ backgroundColor: '#4a274f', '&:hover': { backgroundColor: '#3c1f40' } }}
      >
        Invoice + Envelope
      </Button>
    </DialogActions>
  </Dialog>
);

const DeleteInvoiceDialog = ({
  open,
  invoiceNumber,
  deleting,
  onClose,
  onConfirm,
}: {
  open: boolean;
  invoiceNumber: string;
  deleting: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) => (
  <Dialog open={open} onClose={deleting ? undefined : onClose} aria-labelledby="delete-invoice-dialog-title" fullWidth maxWidth="xs">
    <DialogTitle id="delete-invoice-dialog-title" sx={{ fontWeight: 800 }}>
      Delete invoice #{invoiceNumber}?
    </DialogTitle>
    <DialogContent>
      <p className="pt-2 text-sm text-gray-600 dark:text-gray-300">
        This action cannot be undone. The invoice record and its related detail view will be removed.
      </p>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 3 }}>
      <Button onClick={onClose} disabled={deleting}>
        Cancel
      </Button>
      <Button onClick={onConfirm} color="error" variant="contained" disabled={deleting}>
        {deleting ? 'Deleting...' : 'Delete invoice'}
      </Button>
    </DialogActions>
  </Dialog>
);

export default InvoiceDetailPage;